import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from './components/layout/AppShell';
import { ProtectedRoute } from './components/guards/ProtectedRoute';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ColumnsIndexPage } from './pages/columns/ColumnsIndexPage';
import { ColumnDesignPage } from './pages/columns/ColumnDesignPage';
import { BeamsIndexPage } from './pages/beams/BeamsIndexPage';
import { BeamDesignPage } from './pages/beams/BeamDesignPage';
import { LosasIndexPage } from './pages/losas/LosasIndexPage';
import { LosaDesignPage } from './pages/losas/LosaDesignPage';
import { WallsIndexPage } from './pages/muros/WallsIndexPage';
import { WallDesignPage } from './pages/muros/WallDesignPage';
import { FootingsIndexPage } from './pages/zapatas/FootingsIndexPage';
import { FootingsPage } from './pages/zapatas/FootingsPage';
import { PredimIndexPage } from './pages/predim/PredimIndexPage';
import { PredimPage } from './pages/predim/PredimPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { NormasPage } from './pages/normas/NormasPage';
import { AdminPage } from './pages/admin/AdminPage';
import { BillingPage } from './pages/billing/BillingPage';
import { CartPage } from './pages/billing/CartPage';
import { PlansPage } from './pages/billing/PlansPage';
import { AnalysisIndexPage } from './pages/analysis/AnalysisIndexPage';
import { BeamsAnalysisIndexPage } from './pages/analysis/beams/BeamsAnalysisIndexPage';
import { BeamAnalysisPage } from './pages/analysis/beams/BeamAnalysisPage';
import { SlabsAnalysisIndexPage } from './pages/analysis/slabs/SlabsAnalysisIndexPage';
import { SlabAnalysisPage } from './pages/analysis/slabs/SlabAnalysisPage';
import { ColumnsAnalysisIndexPage } from './pages/analysis/columns/ColumnsAnalysisIndexPage';
import { ColumnAnalysisPage } from './pages/analysis/columns/ColumnAnalysisPage';
import { WallsAnalysisIndexPage } from './pages/analysis/walls/WallsAnalysisIndexPage';
import { WallAnalysisPage } from './pages/analysis/walls/WallAnalysisPage';
import { FootingsAnalysisIndexPage } from './pages/analysis/footings/FootingsAnalysisIndexPage';
import { FootingAnalysisPage } from './pages/analysis/footings/FootingAnalysisPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/legal/PrivacyPage';
import { RefundsPage } from './pages/legal/RefundsPage';
import { CookiesPage } from './pages/legal/CookiesPage';
import { FaqPage } from './pages/legal/FaqPage';
import { ComplaintsPage } from './pages/legal/ComplaintsPage';
import { ContactPage } from './pages/legal/ContactPage';
import { NotFoundPage } from './pages/NotFoundPage';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <LandingPage />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    // Public plan catalog — Culqi.js requires HTTPS in prod but the SPA flow
    // is the same in dev sandbox.
    path: '/planes',
    element: <PlansPage />,
  },
  {
    // Página dedicada del carrito — flujo de compra accesible por URL (Culqi).
    path: '/carrito',
    element: <CartPage />,
  },
  { path: '/terminos', element: <TermsPage /> },
  { path: '/privacidad', element: <PrivacyPage /> },
  { path: '/reembolsos', element: <RefundsPage /> },
  { path: '/cookies', element: <CookiesPage /> },
  { path: '/faq', element: <FaqPage /> },
  { path: '/reclamaciones', element: <ComplaintsPage /> },
  { path: '/contacto', element: <ContactPage /> },
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/app/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },
      {
        // Columns module: index page = list of saved designs, /new = blank
        // form, /:id = load and edit a specific saved design.
        path: 'columns',
        children: [
          { index: true, element: <ColumnsIndexPage /> },
          { path: 'new', element: <ColumnDesignPage /> },
          { path: ':id', element: <ColumnDesignPage /> },
        ],
      },
      {
        // Each design module follows the same pattern:
        //   /app/{module}        → list of saved designs
        //   /app/{module}/new    → blank design form
        //   /app/{module}/:id    → load and edit a saved design
        path: 'beams',
        children: [
          { index: true, element: <BeamsIndexPage /> },
          { path: 'new', element: <BeamDesignPage /> },
          { path: ':id', element: <BeamDesignPage /> },
        ],
      },
      {
        path: 'slabs',
        children: [
          { index: true, element: <LosasIndexPage /> },
          { path: 'new', element: <LosaDesignPage /> },
          { path: ':id', element: <LosaDesignPage /> },
        ],
      },
      {
        path: 'walls',
        children: [
          { index: true, element: <WallsIndexPage /> },
          { path: 'new', element: <WallDesignPage /> },
          { path: ':id', element: <WallDesignPage /> },
        ],
      },
      {
        path: 'footings',
        children: [
          { index: true, element: <FootingsIndexPage /> },
          { path: 'new', element: <FootingsPage /> },
          { path: ':id', element: <FootingsPage /> },
        ],
      },
      {
        // Predim mirrors the structural design modules: index = list,
        // /new = blank hub, /:id = hub with saved design loaded.
        path: 'predim',
        children: [
          { index: true, element: <PredimIndexPage /> },
          { path: 'new', element: <PredimPage /> },
          { path: ':id', element: <PredimPage /> },
        ],
      },
      {
        // Análisis estructural — calcula Mu/Vu/Pu a partir de cargas, los
        // alimenta al módulo de Diseño correspondiente vía prefill.
        path: 'analysis',
        children: [
          { index: true, element: <AnalysisIndexPage /> },
          {
            path: 'beams',
            children: [
              { index: true, element: <BeamsAnalysisIndexPage /> },
              { path: 'new', element: <BeamAnalysisPage /> },
              { path: ':id', element: <BeamAnalysisPage /> },
            ],
          },
          {
            path: 'slabs',
            children: [
              { index: true, element: <SlabsAnalysisIndexPage /> },
              { path: 'new', element: <SlabAnalysisPage /> },
              { path: ':id', element: <SlabAnalysisPage /> },
            ],
          },
          {
            path: 'columns',
            children: [
              { index: true, element: <ColumnsAnalysisIndexPage /> },
              { path: 'new', element: <ColumnAnalysisPage /> },
              { path: ':id', element: <ColumnAnalysisPage /> },
            ],
          },
          {
            path: 'walls',
            children: [
              { index: true, element: <WallsAnalysisIndexPage /> },
              { path: 'new', element: <WallAnalysisPage /> },
              { path: ':id', element: <WallAnalysisPage /> },
            ],
          },
          {
            path: 'footings',
            children: [
              { index: true, element: <FootingsAnalysisIndexPage /> },
              { path: 'new', element: <FootingAnalysisPage /> },
              { path: ':id', element: <FootingAnalysisPage /> },
            ],
          },
        ],
      },
      { path: 'normas', element: <NormasPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'billing', element: <BillingPage /> },
      {
        path: 'admin',
        element: (
          <ProtectedRoute requirePermissions={['users.read', 'roles.read', 'audit.read']}>
            <AdminPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
